import { useEffect } from "react";
import { useForm, useFieldArray, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";

import { useCreateDomain } from "../../hooks/useCreateDomain";
import { PageCreationType } from "../../models/enums/PageCreationType";
import {
  DomainCreate,
  DomainCreateConstructor,
} from "../../models/DomainCreate";
import { EntityTypeLink } from "../../../setting/models/enums/EntityTypeLink";

import { useApiFieldErrors } from "../../../../hooks/fetch/useApiFieldErrors";
import { useFieldRules } from "../../../../hooks/useFieldRules";

import FormCardWrapper from "../../../../components/reusable-components/form/FormCardWrapper";
import FormTextField from "../../../../components/reusable-components/form/MuiFormInput";
import SubmitButton from "../../../../components/reusable-components/form/SubmitButton";
import PageCreationTypeSelect from "../form/PageCreationTypeSelect";
import SectionInputs from "../form/SectionInputs";
import ArrayFieldWrapper from "../../../../components/reusable-components/form/ArrayFieldWrapper";
import EntitySettingsForm from "../../../setting/components/EntitySettingsForm";

const CreateDomain = () => {
  const navigate = useNavigate();
  const rules = useFieldRules();

  const {
    handleSubmit,
    control,
    register,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<DomainCreateConstructor>({
    defaultValues: {
      name: "",
      domain_url: "",
      page_creation_type: PageCreationType.Manual,
      sections: [],
      settings: [],
    },
    mode: "all",
  });

  const {
    fetch: { fetchData, success, error },
  } = useCreateDomain();

  useApiFieldErrors(error, setError);

  const sectionFieldArray = useFieldArray({
    control,
    name: "sections",
  });

  const watchPageCreationType = watch("page_creation_type");
  const isLoading = isSubmitting;

  useEffect(() => {
    if (
      watchPageCreationType === PageCreationType.Manual &&
      sectionFieldArray.fields.length
    ) {
      sectionFieldArray.remove();
    }
  }, [watchPageCreationType]);

  const onSubmit: SubmitHandler<DomainCreateConstructor> = async (formData) => {
    clearErrors();
    const domainForm = new DomainCreate(formData).toFormData();
    const result = await fetchData({ body: domainForm });

    if (result?.id) {
      reset();
      navigate(`/domains/edit/${result.id}`);
    }
  };

  return (
    <FormCardWrapper
      title="Create Domain"
      successText="Domain created successfully!"
      success={success}
      error={error}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={6}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="name"
            label="Name"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Name").required().maxLength(254).get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="domain_url"
            label="Domain URL"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Domain URL").required().domain().get()}
          />
        </Grid>

        <Grid size={12}>
          <PageCreationTypeSelect
            name="page_creation_type"
            control={control}
            selectProps={{ disabled: isLoading }}
          />
        </Grid>

        <Grid size={12}>
          <ArrayFieldWrapper
            label="Sections"
            fieldArray={sectionFieldArray}
            defaultItem={{
              name: "",
              category_file: {} as FileList,
              content_file: {} as FileList,
            }}
            disabled={
              isLoading || watchPageCreationType === PageCreationType.Manual
            }
            renderItem={(index) => (
              <SectionInputs
                disabled={
                  isLoading || watchPageCreationType === PageCreationType.Manual
                }
                control={control}
                index={index}
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        <Grid size={12}>
          <EntitySettingsForm
            entityType={EntityTypeLink.DOMAINS}
            control={control}
            disabled={isLoading}
          />
        </Grid>

        <Grid size={12}>
          <SubmitButton
            text="Create Domain"
            loading={isLoading}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
    </FormCardWrapper>
  );
};

export default CreateDomain;
